// src/services/DataTransformationBridge.ts
/**
 * 🌉 PONTE tra EnhancedColumnIdentifier e TradingMetricsEngine
 * 
 * Questo file risolve il problema "N/A" collegando:
 * CSV raw → EnhancedColumnIdentifier → StandardTrade → TradingMetricsEngine
 */

import { EnhancedColumnIdentifier, ColumnAnalysisResult } from './EnhancedColumnIdentifier';
import TradingMetricsEngine from './TradingMetricsEngine';
import { StandardTrade } from '../types/database';

export interface CSVData {
  headers: string[];
  rows: string[][];
}

export interface TransformationResult {
  trades: StandardTrade[];
  kpis: ReturnType<typeof TradingMetricsEngine.calculate>;
  analysisResult: ColumnAnalysisResult;
  errors: string[];
  warnings: string[];
}

/**
 * Bridge Service - Collega tutto il flusso dati
 */
export class DataTransformationBridge {
  
  /**
   * 🎯 METODO PRINCIPALE
   * Trasforma CSV raw in KPI calcolati
   *
   * @param csvData - Dati CSV grezzi (headers + rows)
   * @param options - Opzioni di trasformazione (es. allowEmpty per file vuoti)
   * @returns Trades mappati + KPI calcolati + diagnostica
   */
  static transform(csvData: CSVData, options?: { allowEmpty?: boolean }): TransformationResult {
    console.log('🌉 DataTransformationBridge: Inizio trasformazione...');
    console.log(`📊 CSV: ${csvData.headers.length} colonne, ${csvData.rows.length} righe`);
    
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // ============================================
    // STEP 1: Analizza colonne con EnhancedColumnIdentifier
    // ============================================
    console.log('🔍 STEP 1: Analisi colonne...');
    
    const analysisResult = EnhancedColumnIdentifier.analyze(
      csvData.headers,
      csvData.rows.slice(0, 10) // Primi 10 righe per analisi
    );
    
    console.log(`✅ Analisi completata:`);
    console.log(`   - Platform: ${analysisResult.detectedPlatform || 'Generic'}`);
    console.log(`   - Confidence: ${(analysisResult.confidence * 100).toFixed(1)}%`);
    console.log(`   - Mapped: ${Object.keys(analysisResult.mapping).length}/${csvData.headers.length}`);
    console.log(`   - Unmapped: ${analysisResult.unmappedColumns.join(', ')}`);
    
    if (analysisResult.confidence < 0.6) {
      warnings.push(
        `⚠️ Confidence bassa (${(analysisResult.confidence * 100).toFixed(0)}%). ` +
        `Alcune colonne potrebbero essere mappate incorrettamente.`
      );
    }
    
    // Verifica campi obbligatori — blocca immediatamente se mancanti (BUG 3)
    if (!options?.allowEmpty) {
      const mappedFields = Object.values(analysisResult.mapping);
      // 'date' can be derived from 'entryTime' or 'exitTime' (e.g. OpenTime/CloseTime columns)
      const hasDateInfo = mappedFields.includes('date') ||
                          mappedFields.includes('entryTime') ||
                          mappedFields.includes('exitTime');
      const hasSymbol  = mappedFields.includes('symbol');
      const hasProfit  = mappedFields.includes('profit');

      const missingFields: string[] = [];
      if (!hasDateInfo) missingFields.push('date / entryTime');
      if (!hasSymbol)   missingFields.push('symbol');
      if (!hasProfit)   missingFields.push('profit');

      if (missingFields.length > 0) {
        const availableHeaders = csvData.headers.join(', ');
        errors.push(
          `Colonne obbligatorie non riconosciute: ${missingFields.join(', ')}. ` +
          `Intestazioni trovate: [${availableHeaders}]. ` +
          `Assicurati che il CSV contenga colonne per: data/ora, simbolo e profitto.`
        );
        // Early return — no point processing rows without critical columns
        return {
          trades: [],
          kpis: this.getEmptyKPIs(),
          analysisResult,
          errors,
          warnings
        };
      }
    }
    
    // ============================================
    // STEP 2: Trasforma righe in StandardTrade[]
    // ============================================
    console.log('🔄 STEP 2: Trasformazione righe...');
    
    const trades: StandardTrade[] = [];
    let skippedRows = 0;
    
    csvData.rows.forEach((row, rowIndex) => {
      try {
        const trade: Partial<StandardTrade> = {
          // Default values — entryPrice/exitPrice NON inizializzati a 0:
          // se restano undefined dopo il mapping, il DB salva NULL
          // e l'utente vede il problema nel mapping UI
          type: 'BUY',
          quantity: 0,
          profit: 0
        };

        // 🔧 FIX: Estrai data SOLO se non è già presente nel CSV
        // standardTradingDataManager potrebbe averla già estratta
        const dateHeader = Object.keys(analysisResult.mapping).find(h => analysisResult.mapping[h] === 'date');
        if (dateHeader) {
          const dateIndex = csvData.headers.indexOf(dateHeader);
          if (dateIndex !== -1) {
            const rawDate = row[dateIndex] || '';
            if (rawDate) {
              trade.date = rawDate; // Usa la data già estratta da standardTradingDataManager
              console.log(`✅ [Row ${rowIndex + 1}] trade.date from CSV: "${trade.date}"`);
            }
          }
        }

        // Se NON c'è una colonna 'date', prova a estrarre da entryTime o exitTime
        const entryTimeHeader = Object.keys(analysisResult.mapping).find(h => analysisResult.mapping[h] === 'entryTime');
        let exitTimeHeader = Object.keys(analysisResult.mapping).find(h => analysisResult.mapping[h] === 'exitTime');

        // 🔧 FALLBACK: Se exitTimeHeader non trovato nel mapping, cerca direttamente negli headers
        if (!exitTimeHeader) {
          // Cerca varianti comuni di "Closing time"
          const exitTimeVariants = ['Closing time', 'Closing Time', 'closing time', 'CLOSING TIME'];
          exitTimeHeader = csvData.headers.find(h => exitTimeVariants.includes(h)) || undefined;

          if (exitTimeHeader) {
            console.log(`🔧 FALLBACK: Trovato "${exitTimeHeader}" negli headers (non nel mapping)`);
          }
        }

        // 🔍 DEBUG: Log mapping per vedere quali colonne sono state riconosciute
        if (rowIndex === 0) {
          console.log(`\n🔍 COLUMN MAPPING RICONOSCIUTO:`);
          console.log(`   - CSV Headers:`, csvData.headers);
          console.log(`   - Mapping:`, analysisResult.mapping);
          console.log(`   - entryTimeHeader: "${entryTimeHeader}" ${entryTimeHeader ? '✅' : '❌ NON TROVATO'}`);
          console.log(`   - exitTimeHeader: "${exitTimeHeader}" ${exitTimeHeader ? '✅' : '❌ NON TROVATO'}`);
          console.log(`   - dateHeader: "${dateHeader}"\n`);
        }

        // SOLO se trade.date è ancora vuoto, prova a estrarre da entryTime/exitTime
        if (!trade.date && entryTimeHeader) {
          const entryTimeIndex = csvData.headers.indexOf(entryTimeHeader);
          console.log(`🔍 [Row ${rowIndex + 1}] entryTimeIndex: ${entryTimeIndex}`);
          if (entryTimeIndex !== -1) {
            const rawEntryTime = row[entryTimeIndex] || '';
            console.log(`🔍 [Row ${rowIndex + 1}] rawEntryTime: "${rawEntryTime}"`);
            // Pass full datetime string so formatDate/formatTime can detect AM/PM and locale
            if (rawEntryTime) {
              trade.date = EnhancedColumnIdentifier.formatDate(rawEntryTime);
              console.log(`✅ [Row ${rowIndex + 1}] trade.date set to: "${trade.date}"`);
              if (rawEntryTime.includes(' ')) {
                trade.entryTime = EnhancedColumnIdentifier.formatTime(rawEntryTime);
                console.log(`✅ [Row ${rowIndex + 1}] trade.entryTime set to: "${trade.entryTime}"`);
              }
            } else {
              console.log(`⚠️ [Row ${rowIndex + 1}] rawEntryTime is empty`);
            }
          }
        } else if (trade.date) {
          console.log(`✅ [Row ${rowIndex + 1}] trade.date already set, skipping extraction`);
        } else {
          console.log(`⚠️ [Row ${rowIndex + 1}] No entryTimeHeader found in mapping`);
        }

        // Fallback: exitTime (Closing time) - se non c'era Opening time, usa questo per trade.date
        if (!trade.date && exitTimeHeader) {
          const exitTimeIndex = csvData.headers.indexOf(exitTimeHeader);
          console.log(`🔍 [Row ${rowIndex + 1}] exitTimeIndex: ${exitTimeIndex}`);
          if (exitTimeIndex !== -1) {
            const rawExitTime = row[exitTimeIndex] || '';
            console.log(`🔍 [Row ${rowIndex + 1}] rawExitTime: "${rawExitTime}"`);
            // Gestisce sia formato italiano "30/12/2025 21:25:51" che ISO "2024-01-02 09:15:32"
            if (rawExitTime.includes(' ')) {
              trade.date = EnhancedColumnIdentifier.formatDate(rawExitTime);
              console.log(`✅ [Row ${rowIndex + 1}] trade.date set from exitTime to: "${trade.date}"`);
              trade.exitTime = EnhancedColumnIdentifier.formatTime(rawExitTime);
              console.log(`✅ [Row ${rowIndex + 1}] trade.exitTime set to: "${trade.exitTime}"`);
            }
          }
        }

        // 🔧 CRITICAL: Estrai exitDate e exitTime da Closing time (sempre, per calcolare la durata)
        if (exitTimeHeader) {
          const exitTimeIndex = csvData.headers.indexOf(exitTimeHeader);
          if (exitTimeIndex !== -1) {
            const rawExitTime = row[exitTimeIndex] || '';
            // Gestisce sia formato italiano "30/12/2025 21:25:51" che ISO "2024-01-02 09:15:32"
            if (rawExitTime.includes(' ')) {
              // Estrai EXIT DATE → trade.exitDate (per calcolare durata)
              trade.exitDate = EnhancedColumnIdentifier.formatDate(rawExitTime);
              console.log(`✅ [Row ${rowIndex + 1}] trade.exitDate set to: "${trade.exitDate}"`);

              // Estrai EXIT TIME → trade.exitTime
              trade.exitTime = EnhancedColumnIdentifier.formatTime(rawExitTime);
              console.log(`✅ [Row ${rowIndex + 1}] trade.exitTime set to: "${trade.exitTime}"`);

            } else if (rawExitTime) {
              // Se c'è un valore ma non in formato data+ora, potrebbe essere solo orario
              // Es: "01:30:21.010" → combina con trade.date da Opening time
              if (/^\d{1,2}:\d{2}/.test(rawExitTime)) {
                trade.exitTime = EnhancedColumnIdentifier.formatTime(rawExitTime);
                console.log(`✅ [Row ${rowIndex + 1}] trade.exitTime (time-only) set to: "${trade.exitTime}"`);

                // Se abbiamo trade.date da Opening time, usa quella per exitDate
                if (trade.date) {
                  trade.exitDate = trade.date;
                  console.log(`✅ [Row ${rowIndex + 1}] trade.exitDate set from trade.date: "${trade.exitDate}"`);
                }
              } else {
                console.log(`⚠️ [Row ${rowIndex + 1}] rawExitTime format not recognized: "${rawExitTime}"`);
              }
            }
          }
        } else {
          // 🔍 DEBUG: exitTimeHeader non trovato
          if (rowIndex === 0) {
            console.log(`⚠️ ATTENZIONE: Colonna "Closing time" NON trovata nel CSV!`);
            console.log(`   Il sistema cercava queste colonne per exit time:`, [
              'Closing time', 'Closing Time', 'Orario di chiusura',
              'Data chiusura', 'exit_time', 'exitTime'
            ]);
          }
        }

        // Mappa ogni colonna usando il mapping identificato
        csvData.headers.forEach((header, colIndex) => {
          const mappedField = analysisResult.mapping[header];

          if (mappedField) {
            const rawValue = row[colIndex] || '';

            // Usa EnhancedColumnIdentifier per trasformare il valore
            const transformedValue = EnhancedColumnIdentifier.transformValue(
              mappedField,
              rawValue
            );

            // Skip already-extracted fields to prevent the column loop from overwriting
            // correctly parsed values with the raw combined datetime string
            if (mappedField === 'date' && trade.date) return;
            if (mappedField === 'entryTime' && trade.entryTime) return;
            if (mappedField === 'exitTime' && trade.exitTime) return;

            trade[mappedField] = transformedValue;
          }
        });

        // 🆕 Auto‑genera ID se mancante
        if (!trade.id || (typeof trade.id === 'string' && trade.id.trim() === '')) {
          const dateStr = trade.date ? String(trade.date).replace(/[^\d]/g, '') : '';
          const symbolStr = trade.symbol ? String(trade.symbol).toUpperCase().slice(0, 6) : 'TRD';
          const ts = Date.now();
          trade.id = `${symbolStr}_${dateStr}_${ts}_${rowIndex}`;
          console.log(`✅ ID auto‑generato per riga ${rowIndex + 1}: ${trade.id}`);
        }

        // Valida campi minimi (date, symbol)
        if (!trade.date || !trade.symbol) {
          skippedRows++;
          console.warn(`⚠️ Riga ${rowIndex + 1} saltata: campi obbligatori mancanti (date/symbol)`);
          return;
        }

        // Aggiungi trade valido
        trades.push(trade as StandardTrade);
        
      } catch (error) {
        skippedRows++;
        console.error(`❌ Errore riga ${rowIndex + 1}:`, error);
        errors.push(`Riga ${rowIndex + 1}: ${(error as Error).message}`);
      }
    });
    
    console.log(`✅ Trasformazione completata: ${trades.length} trade validi, ${skippedRows} righe saltate`);
    
    if (skippedRows > 0) {
      warnings.push(
        `⚠️ ${skippedRows} righe saltate per dati mancanti o invalidi. ` +
        `Verifica che tutte le righe abbiano almeno: date, symbol, profit.`
      );
    }
    
    // Skip "no trades" validation if allowEmpty is true
    if (trades.length === 0 && !options?.allowEmpty) {
      errors.push(
        `❌ Nessun trade valido trovato! Controlla che il CSV abbia: ` +
        `\n   - Header con nomi colonne ` +
        `\n   - Almeno le colonne: date, symbol, profit ` +
        `\n   - Dati validi in ogni riga`
      );
    }
    
    // ============================================
    // STEP 3: Calcola KPI con TradingMetricsEngine
    // ============================================
    console.log('📊 STEP 3: Calcolo KPI...');
    
    let kpis;
    
    if (trades.length > 0) {
      try {
        kpis = TradingMetricsEngine.calculate(trades);
        console.log(`✅ KPI calcolati con successo`);
        console.log(`   - Total Trades: ${kpis.performance.totalTrades}`);
        console.log(`   - Win Rate: ${kpis.performance.winRate.toFixed(2)}%`);
        console.log(`   - Profit Factor: ${kpis.performance.profitFactor.toFixed(3)}`);
        console.log(`   - Total P&L: €${kpis.performance.totalNetPnl.toFixed(2)}`);
      } catch (error) {
        console.error('❌ Errore calcolo KPI:', error);
        errors.push(`Errore calcolo metriche: ${(error as Error).message}`);
        
        // Fallback: KPI vuoti
        kpis = this.getEmptyKPIs();
      }
    } else {
      console.warn('⚠️ Nessun trade disponibile, KPI vuoti');
      kpis = this.getEmptyKPIs();
    }
    
    // ============================================
    // STEP 4: Return risultato completo
    // ============================================
    console.log('🎉 Trasformazione completata!');
    
    return {
      trades,
      kpis,
      analysisResult,
      errors,
      warnings
    };
  }
  
  /**
   * KPI vuoti per fallback
   */
  private static getEmptyKPIs() {
    return TradingMetricsEngine.calculate([]);
  }
  
  /**
   * 🧪 Test rapido del bridge
   * Usa questo per verificare che tutto funzioni
   */
  static test(csvData: CSVData): void {
    console.log('🧪 TEST DataTransformationBridge');
    console.log('================================');
    
    const result = this.transform(csvData);
    
    console.log('\n📊 RISULTATI:');
    console.log(`   - Trades: ${result.trades.length}`);
    console.log(`   - Platform: ${result.analysisResult.detectedPlatform || 'Generic'}`);
    console.log(`   - Confidence: ${(result.analysisResult.confidence * 100).toFixed(1)}%`);
    console.log(`   - Errori: ${result.errors.length}`);
    console.log(`   - Warning: ${result.warnings.length}`);
    
    if (result.errors.length > 0) {
      console.log('\n❌ ERRORI:');
      result.errors.forEach(err => console.log(`   ${err}`));
    }
    
    if (result.warnings.length > 0) {
      console.log('\n⚠️ WARNING:');
      result.warnings.forEach(warn => console.log(`   ${warn}`));
    }
    
    if (result.trades.length > 0) {
      console.log('\n✅ KPI CALCOLATI:');
      console.log(`   - Win Rate: ${result.kpis.performance.winRate.toFixed(2)}%`);
      console.log(`   - Profit Factor: ${result.kpis.performance.profitFactor.toFixed(3)}`);
      console.log(`   - Total P&L: €${result.kpis.performance.totalNetPnl.toFixed(2)}`);
    }
    
    console.log('\n================================');
  }
}

export default DataTransformationBridge;
